import axios from 'axios'
import { toast, Slide } from 'react-toastify'
import { ApiErrorToast } from 'src/core/components/toast'
import { store } from 'src/redux/storeConfig/store'
import ApiConfig from '../configs'
import { getFingerprintIdSync, getFingerprintIdAsync } from 'src/utils/fingerprint'

export default class Service {
	jwtConfig = { ...ApiConfig }
	isAlreadyFetchingAccessToken = false
	subscribers = []

	constructor(jwtOverrideConfig) {
		this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }
		axios.interceptors.request.use(
			async (config) => {
				config.baseURL = ApiConfig.apiUrl
				// config.headers.admin = true;
				// config.headers.token = process.env.REACT_APP_ADMIN_TOKEN;
				// config.headers.token = token;
				// if (accessToken) {
				//     config.headers.authorization = accessToken;
				// }
				const token = localStorage.getItem('token')
				// const accessToken = this.getToken();
				if (token) {
					config.headers.Authorization = `Bearer ${token}`
				}
				// Add fingerprint ID (fpid) header
				let fpid = getFingerprintIdSync()
				if (!fpid) {
					// If not cached, generate it (this will cache it for future use)
					fpid = await getFingerprintIdAsync()
				}
				if (fpid) {
					config.headers.fpid = fpid
				}
				return config
			},
			(error) => Promise.reject(error),
		)
		axios.interceptors.response.use(
			(response) => response,
			(error) => {
				const { response } = error
				if (response && response.status === 400) {
					const msg = `${response.data.error}`
					toast.error(<ApiErrorToast title={msg} />, {
						transition: Slide,
						hideProgressBar: true,
						autoClose: 5000,
					})
				} else if (response && response.status === 401) {
					store.dispatch({ type: 'LOGOUT' })
				} else {
					const msg = `${response.data.error}`
					// toast.error(<ApiErrorToast title={msg} />, {
					//     transition: Slide,
					//     hideProgressBar: true,
					//     autoClose: 5000
					// });
				}
				return Promise.reject(error)
			},
		)
	}

	/**
	 * Authentication
	 */
	getToken() {
		return store.getState().auth.token
	}

	signIn(...args) {
		return axios.post(this.jwtConfig.SignIn, ...args)
	}

	signOut(...args) {
		return axios.delete(this.jwtConfig.SignOut, ...args)
	}

	/**
	 * Players
	 */
	getPlayersList(args) {
		return axios.get(`${this.jwtConfig.Players}/`, { params: { ...args } })
	}

	getPlayerUserNameOption(args) {
		return axios.get(`${this.jwtConfig.Players}/options/username`, { params: { ...args } })
	}

	getUserAddressOption(args) {
		return axios.get(`${this.jwtConfig.Players}/options/address`, { params: { ...args } })
	}

	createPlayer(...args) {
		return axios.post(this.jwtConfig.Players, ...args)
	}

	getPlayer(id) {
		return axios.get(`${this.jwtConfig.Players}/player/${id}`)
	}

	updatePlayer(id, args) {
		return axios.put(`${this.jwtConfig.Players}/player/${id}`, args)
	}

	deletePlayer(id) {
		return axios.delete(`${this.jwtConfig.Players}/player/${id}`)
	}

	blockPlayer(args) {
		return axios.post(`${this.jwtConfig.Players}/player/block`, args)
	}

	allowPlayer(args) {
		return axios.post(`${this.jwtConfig.Players}/player/allow`, args)
	}

	updatePassword(...args) {
		return axios.put(`${this.jwtConfig.Players}/password`, ...args)
	}

	/**
	 * Login History
	 */
	getLoginHistorysList(...args) {
		return axios.post(`${this.jwtConfig.LoginHistory}/list`, ...args)
	}

	getLoginHistorysLabel(...args) {
		return axios.post(`${this.jwtConfig.LoginHistory}/label`, ...args)
	}

	createLoginHistory(...args) {
		return axios.post(this.jwtConfig.LoginHistory, ...args)
	}

	getLoginHistory(id) {
		return axios.get(`${this.jwtConfig.LoginHistory}/${id}`)
	}

	updateLoginHistory(id, args) {
		return axios.put(`${this.jwtConfig.LoginHistory}/${id}`, args)
	}

	deleteLoginHistory(id) {
		return axios.delete(`${this.jwtConfig.LoginHistory}/${id}`)
	}

	getPlayerLoginHistoryList(args) {
		return axios.get(`${this.jwtConfig.LoginHistory}`, { params: { ...args } })
	}

	getAffiliateLoginHistoryList(args) {
		return axios.get(`${this.jwtConfig.AffiliateLoginHistory}`, { params: { ...args } })
	}

	/**
	 * Online Players
	 */
	getOnlinesList(...args) {
		return axios.get(`${this.jwtConfig.OnlinePlayers}`, ...args)
	}

	getOnlinesLabel(...args) {
		return axios.post(`${this.jwtConfig.OnlinePlayers}/label`, ...args)
	}

	createOnline(...args) {
		return axios.post(this.jwtConfig.OnlinePlayers, ...args)
	}

	getOnline(id) {
		return axios.get(`${this.jwtConfig.OnlinePlayers}/${id}`)
	}

	updateOnline(id, args) {
		return axios.put(`${this.jwtConfig.OnlinePlayers}/${id}`, args)
	}

	deleteOnline(id) {
		return axios.delete(`${this.jwtConfig.OnlinePlayers}/${id}`)
	}

	deleteAllOnline() {
		return axios.delete(this.jwtConfig.OnlinePlayers)
	}

	/**
	 * Player Referral
	 */
	getPlayerReferralList(args) {
		return axios.get(`${this.jwtConfig.Referrals}`, { params: { ...args } })
	}

	getPlayerCommissionLevelList(args) {
		return axios.get(`${this.jwtConfig.PlayerCommissionLevels}`, { params: { ...args } })
	}

	getPlayerCommissionLevel(id) {
		return axios.get(`${this.jwtConfig.PlayerCommissionLevels}/${id}`)
	}

	createPlayerCommissionLevel(args) {
		return axios.post(this.jwtConfig.PlayerCommissionLevels, args)
	}

	updatePlayerCommissionLevel(id, args) {
		return axios.put(`${this.jwtConfig.PlayerCommissionLevels}/${id}`, args)
	}

	deletePlayerCommissionLevel(id) {
		return axios.delete(`${this.jwtConfig.PlayerCommissionLevels}/${id}`)
	}

	getPlayerReferralRewardHistoryList(args) {
		return axios.get(`${this.jwtConfig.PlayerReferralRewardHistory}`, { params: { ...args } })
	}

	/**
	 * Affiliates
	 */
	getAffiliatesList(args) {
		return axios.get(`${this.jwtConfig.Affiliates}/`, { params: { ...args } })
	}

	getAffiliateUserNameOption(args) {
		return axios.get(`${this.jwtConfig.Affiliates}/options/username`, { params: { ...args } })
	}

	createAffiliate(...args) {
		return axios.post(this.jwtConfig.Affiliates, ...args)
	}

	getAffiliate(id) {
		return axios.get(`${this.jwtConfig.Affiliates}/affiliate/${id}`)
	}

	updateAffiliate(id, args) {
		return axios.put(`${this.jwtConfig.Affiliates}/affiliate/${id}`, args)
	}

	deleteAffiliate(id) {
		return axios.delete(`${this.jwtConfig.Affiliates}/affiliate/${id}`)
	}

	/**
	 * Online Affiliates
	 */
	getOnlineAffiliatesList(...args) {
		return axios.get(`${this.jwtConfig.OnlineAffiliates}`, ...args)
	}

	getOnlineAffiliate(id) {
		return axios.get(`${this.jwtConfig.OnlineAffiliates}/${id}`)
	}

	updateOnlineAffiliate(id, args) {
		return axios.put(`${this.jwtConfig.OnlineAffiliates}/${id}`, args)
	}

	deleteOnlineAffiliate(id) {
		return axios.delete(`${this.jwtConfig.OnlineAffiliates}/${id}`)
	}

	deleteAllOnlineAffiliates() {
		return axios.delete(this.jwtConfig.OnlineAffiliates)
	}

	/**
	 * Affiliate Referral
	 */
	getAffiliateNetwork(args) {
		return axios.get(`${this.jwtConfig.Affiliates}/network`, { params: { ...args } })
	}

	getAffiliateReferral(args) {
		return axios.get(`${this.jwtConfig.Affiliates}/referral`, { params: { ...args } })
	}

	getAffiliateLevelOption(args) {
		return axios.get(`${this.jwtConfig.Affiliates}/options/level`, { params: { ...args } })
	}

	getAffiliateLevelsList(args) {
		return axios.get(`${this.jwtConfig.Affiliates}/level`, { params: { ...args } })
	}

	getAffiliateLevel(id) {
		return axios.get(`${this.jwtConfig.Affiliates}/level/${id}`)
	}

	createAffiliateLevel(args) {
		return axios.post(`${this.jwtConfig.Affiliates}/level`, args)
	}

	updateAffiliateLevel(id, args) {
		return axios.put(`${this.jwtConfig.Affiliates}/level/${id}`, args)
	}

	deleteAffiliateLevel(id) {
		return axios.delete(`${this.jwtConfig.Affiliates}/level/${id}`)
	}

	getAffiliateCommissionLevelList(args) {
		return axios.get(`${this.jwtConfig.Affiliates}/commission-level`, { params: { ...args } })
	}

	getAffiliateCommissionLevel(id) {
		return axios.get(`${this.jwtConfig.Affiliates}/commission-level/${id}`)
	}

	createAffiliateCommissionLevel(args) {
		return axios.post(`${this.jwtConfig.Affiliates}/commission-level`, args)
	}

	updateAffiliateCommissionLevel(id, args) {
		return axios.put(`${this.jwtConfig.Affiliates}/commission-level/${id}`, args)
	}

	deleteAffiliateCommissionLevel(id) {
		return axios.delete(`${this.jwtConfig.Affiliates}/commission-level/${id}`)
	}

	getAffiliateRewardHistoryList(args) {
		return axios.get(`${this.jwtConfig.RewardHistory}`, { params: { ...args } })
	}

	/**
	 * Role
	 */
	getRolesList(...args) {
		return axios.post(`${this.jwtConfig.Roles}/list`, ...args)
	}

	getRolesOption(...args) {
		return axios.get(`${this.jwtConfig.Roles}/options`, ...args)
	}

	createRole(...args) {
		return axios.post(this.jwtConfig.Roles, ...args)
	}

	getRole(id) {
		return axios.get(`${this.jwtConfig.Roles}/${id}`)
	}

	updateRole(id, args) {
		return axios.put(`${this.jwtConfig.Roles}/${id}`, args)
	}

	deleteRole(id) {
		return axios.delete(`${this.jwtConfig.Roles}/${id}`)
	}

	/**
	 * Currencies
	 */
	getCurrenciesList(args) {
		return axios.get(`${this.jwtConfig.Currencies}/`, { params: { ...args } })
	}

	getCurrenciesOption(...args) {
		return axios.get(`${this.jwtConfig.Currencies}/options`, ...args)
	}

	createCurrency(...args) {
		return axios.post(`${this.jwtConfig.Currencies}/`, ...args)
	}

	getCurrency(id) {
		return axios.get(`${this.jwtConfig.Currencies}/${id}`)
	}

	updateCurrency(id, args) {
		return axios.put(`${this.jwtConfig.Currencies}/${id}`, args)
	}

	deleteCurrency(id) {
		return axios.delete(`${this.jwtConfig.Currencies}/${id}`)
	}

	/**
	 * Payments
	 */
	getPaymentsList(args) {
		return axios.get(`${this.jwtConfig.Payments}`, { params: { ...args } })
	}

	createPayment(...args) {
		return axios.post(this.jwtConfig.Payments, ...args)
	}

	getPayment(id) {
		return axios.get(`${this.jwtConfig.Payments}/${id}`)
	}

	updatePayment(id, args) {
		return axios.put(`${this.jwtConfig.Payments}/${id}`, args)
	}

	deletePayment(id) {
		return axios.delete(`${this.jwtConfig.Payments}/${id}`)
	}

	approvePayment(id, ...args) {
		return axios.post(`${this.jwtConfig.Payments}/${id}`, ...args)
	}

	/**
	 * Balances
	 */
	getBalancesList(args) {
		return axios.get(`${this.jwtConfig.Balance}`, { params: { ...args } })
	}

	createBalance(...args) {
		return axios.post(this.jwtConfig.Balance, ...args)
	}

	getBalance(id) {
		return axios.get(`${this.jwtConfig.Balance}/${id}`)
	}

	updateBalance(id, args) {
		return axios.put(`${this.jwtConfig.Balance}/${id}`, args)
	}

	toggleBalance(id, args) {
		return axios.post(`${this.jwtConfig.Balance}/${id}`, args)
	}

	deleteBalance(id) {
		return axios.delete(`${this.jwtConfig.Balance}/${id}`)
	}

	/**
	 * Balance History
	 */
	getBalanceHistorysList(args) {
		return axios.get(`${this.jwtConfig.BalanceHistory}`, { params: { ...args } })
	}

	createBalanceHistory(...args) {
		return axios.post(this.jwtConfig.BalanceHistory, ...args)
	}

	getBalanceHistory(id) {
		return axios.get(`${this.jwtConfig.BalanceHistory}/${id}`)
	}

	updateBalanceHistory(id, args) {
		return axios.put(`${this.jwtConfig.BalanceHistory}/${id}`, args)
	}

	deleteBalanceHistory(id) {
		return axios.delete(`${this.jwtConfig.BalanceHistory}/${id}`)
	}

	SportsResettle(id) {
		return axios.post(`${this.jwtConfig.SportsResettle}/${id}`)
	}

	/**
	 * Casino Lists
	 */
	syncCasinoList(args) {
		return axios.get(`${this.jwtConfig.Casinos}/sync-casino`, { params: { ...args } })
	}

	syncCasinoTag(args) {
		return axios.get(`${this.jwtConfig.Casinos}/sync-tag`, { params: { ...args } })
	}

	getCasinoList(args) {
		return axios.get(`${this.jwtConfig.CasinosLists}`, { params: { ...args } })
	}

	getCasino(id) {
		return axios.get(`${this.jwtConfig.CasinosLists}/${id}`)
	}

	createCasino(args) {
		return axios.post(this.jwtConfig.CasinosLists, args)
	}

	updateCasino(id, args) {
		return axios.put(`${this.jwtConfig.CasinosLists}/${id}`, args)
	}

	deleteCasino(id) {
		return axios.delete(`${this.jwtConfig.CasinosLists}/${id}`)
	}

	/**
	 * Casino Providers
	 */
	getCasinosProvidersLabel(...args) {
		return axios.post(`${this.jwtConfig.CasinosProviders}/label`, ...args)
	}

	getCasinosProvidersList(...args) {
		return axios.post(`${this.jwtConfig.CasinosProviders}/list`, ...args)
	}

	getCasinoProvidersList(args) {
		return axios.get(`${this.jwtConfig.CasinosProviders}`, { params: { ...args } })
	}

	getCasinoProvider(id) {
		return axios.get(`${this.jwtConfig.CasinosProviders}/${id}`)
	}

	createCasinoProvider(args) {
		return axios.post(this.jwtConfig.CasinosProviders, args)
	}

	updateCasinoProvider(id, args) {
		return axios.put(`${this.jwtConfig.CasinosProviders}/${id}`, args)
	}

	deleteCasinoProvider(id) {
		return axios.delete(`${this.jwtConfig.CasinosProviders}/${id}`)
	}

	getCasinoProviderOption(args) {
		return axios.get(`${this.jwtConfig.CasinosProviders}/options`, { params: { ...args } })
	}

	/**
	 * Casino Types
	 */
	getCasinoTypesList(args) {
		return axios.get(`${this.jwtConfig.CasinosTypes}`, { params: { ...args } })
	}

	getCasinoType(id) {
		return axios.get(`${this.jwtConfig.CasinosTypes}/${id}`)
	}

	createCasinoType(args) {
		return axios.post(this.jwtConfig.CasinosTypes, args)
	}

	updateCasinoType(id, args) {
		return axios.put(`${this.jwtConfig.CasinosTypes}/${id}`, args)
	}

	deleteCasinoType(id) {
		return axios.delete(`${this.jwtConfig.CasinosTypes}/${id}`)
	}

	getCasinoTypeOption(args) {
		return axios.get(`${this.jwtConfig.CasinosTypes}/options`, { params: { ...args } })
	}

	/**
	 * Casino Tag Categories
	 */
	getCasinoTagCategoriesList(args) {
		return axios.get(`${this.jwtConfig.CasinosTagCategories}`, { params: { ...args } })
	}

	getCasinoTagCategory(id) {
		return axios.get(`${this.jwtConfig.CasinosTagCategories}/${id}`)
	}

	createCasinoTagCategory(args) {
		return axios.post(this.jwtConfig.CasinosTagCategories, args)
	}

	updateCasinoTagCategory(id, args) {
		return axios.put(`${this.jwtConfig.CasinosTagCategories}/${id}`, args)
	}

	deleteCasinoTagCategory(id) {
		return axios.delete(`${this.jwtConfig.CasinosTagCategories}/${id}`)
	}

	getCasinoTagCategoryOption(args) {
		return axios.get(`${this.jwtConfig.CasinosTagCategories}/options`, { params: { ...args } })
	}

	/**
	 * Casino Tags
	 */
	getCasinoTagsList(args) {
		return axios.get(`${this.jwtConfig.CasinosTags}`, { params: { ...args } })
	}

	getCasinoTag(id) {
		return axios.get(`${this.jwtConfig.CasinosTags}/${id}`)
	}

	createCasinoTag(args) {
		return axios.post(this.jwtConfig.CasinosTags, args)
	}

	updateCasinoTag(id, args) {
		return axios.put(`${this.jwtConfig.CasinosTags}/${id}`, args)
	}

	deleteCasinoTag(id) {
		return axios.delete(`${this.jwtConfig.CasinosTags}/${id}`)
	}

	/**
	 * Sportsbook Lists
	 */
	syncSportsbookList(args) {
		return axios.get(`${this.jwtConfig.Sportsbooks}/sync-sportsbook`, { params: { ...args } })
	}

	getSportsbookList(args) {
		return axios.get(`${this.jwtConfig.SportsbooksLists}`, { params: { ...args } })
	}

	getSportsbook(id) {
		return axios.get(`${this.jwtConfig.SportsbooksLists}/${id}`)
	}

	createSportsbook(args) {
		return axios.post(this.jwtConfig.SportsbooksLists, args)
	}

	updateSportsbook(id, args) {
		return axios.put(`${this.jwtConfig.SportsbooksLists}/${id}`, args)
	}

	deleteSportsbook(id) {
		return axios.delete(`${this.jwtConfig.SportsbooksLists}/${id}`)
	}

	/**
	 * Sportsbook Providers
	 */
	getSportsbookProvidersList(args) {
		return axios.get(`${this.jwtConfig.SportsbooksProviders}`, { params: { ...args } })
	}

	getSportsbookProvider(id) {
		return axios.get(`${this.jwtConfig.SportsbooksProviders}/${id}`)
	}

	createSportsbookProvider(args) {
		return axios.post(this.jwtConfig.SportsbooksProviders, args)
	}

	updateSportsbookProvider(id, args) {
		return axios.put(`${this.jwtConfig.SportsbooksProviders}/${id}`, args)
	}

	deleteSportsbookProvider(id) {
		return axios.delete(`${this.jwtConfig.SportsbooksProviders}/${id}`)
	}

	getSportsbookProviderOption(args) {
		return axios.get(`${this.jwtConfig.SportsbooksProviders}/options`, { params: { ...args } })
	}

	/**
	 * Sportsbook Types
	 */
	getSportsbookTypesList(args) {
		return axios.get(`${this.jwtConfig.SportsbooksTypes}`, { params: { ...args } })
	}

	getSportsbookType(id) {
		return axios.get(`${this.jwtConfig.SportsbooksTypes}/${id}`)
	}

	createSportsbookType(args) {
		return axios.post(this.jwtConfig.SportsbooksTypes, args)
	}

	updateSportsbookType(id, args) {
		return axios.put(`${this.jwtConfig.SportsbooksTypes}/${id}`, args)
	}

	deleteSportsbookType(id) {
		return axios.delete(`${this.jwtConfig.SportsbooksTypes}/${id}`)
	}

	getSportsbookTypeOption(args) {
		return axios.get(`${this.jwtConfig.SportsbooksTypes}/options`, { params: { ...args } })
	}

	/**
	 * Original Games
	 */
	getOriginalGamesList(args) {
		return axios.get(`${this.jwtConfig.OriginalGamesLists}`, { params: { ...args } })
	}

	getOriginalGame(id) {
		return axios.get(`${this.jwtConfig.OriginalGamesLists}/${id}`)
	}

	createOriginalGame(args) {
		return axios.post(this.jwtConfig.OriginalGamesLists, args)
	}

	updateOriginalGame(id, args) {
		return axios.put(`${this.jwtConfig.OriginalGamesLists}/${id}`, args)
	}

	deleteOriginalGame(id) {
		return axios.delete(`${this.jwtConfig.OriginalGamesLists}/${id}`)
	}

	originalGameFileUpload(formData) {
		return axios.post(`${this.jwtConfig.OriginalGamesLists}/file-upload`, formData)
	}

	/**
	 * Original Games History
	 */
	getOriginalGamesHistoryList(args) {
		return axios.get(`${this.jwtConfig.OriginalGamesHistory}`, { params: { ...args } })
	}

	getGamesHistorysLabel(...args) {
		return axios.get(`${this.jwtConfig.OriginalGamesHistory}/label`, ...args)
	}

	getGamesHistoryEvent(...args) {
		return axios.post(`${this.jwtConfig.OriginalGamesHistory}/events`, ...args)
	}

	createGamesHistory(...args) {
		return axios.post(this.jwtConfig.OriginalGamesHistory, ...args)
	}

	getGamesHistory(id) {
		return axios.get(`${this.jwtConfig.OriginalGamesHistory}/${id}`)
	}

	updateGamesHistory(id, args) {
		return axios.put(`${this.jwtConfig.OriginalGamesHistory}/${id}`, args)
	}

	deleteGamesHistory(id) {
		return axios.delete(`${this.jwtConfig.OriginalGamesHistory}/${id}`)
	}

	/**
	 * P2p
	 */
	getP2pMarketsList(args) {
		return axios.get(`${this.jwtConfig.P2pMarkets}`, { params: { ...args } })
	}

	getP2pMarket(id) {
		return axios.get(`${this.jwtConfig.P2pMarkets}/${id}`)
	}

	resolveMarket(id, ...args) {
		return axios.post(`${this.jwtConfig.P2pMarkets}/${id}/resolve`, ...args)
	}

	createP2pMarket(...args) {
		return axios.post(`${this.jwtConfig.P2pMarkets}`, ...args)
	}

	updateMarket(id, args) {
		return axios.put(`${this.jwtConfig.P2pMarkets}/${id}`, args)
	}

	deleteMarket(id) {
		return axios.delete(`${this.jwtConfig.P2pMarkets}/${id}`)
	}

	p2pResult(args) {
		return axios.post(`${this.jwtConfig.P2pMarkets}/result`, args)
	}

	p2pFileUpload(formData) {
		return axios.post(`${this.jwtConfig.P2pMarkets}/file-upload`, formData)
	}

	getP2pHistory(args) {
		return axios.get(`${this.jwtConfig.P2pHistory}`, { params: { ...args } })
	}

	/**
	 * Loyalty
	 */
	getLoyaltyTierOption(args) {
		return axios.get(`${this.jwtConfig.LoyaltyTiers}/options`, { params: { ...args } })
	}

	getLoyaltyTiersList(args) {
		return axios.get(`${this.jwtConfig.LoyaltyTiers}`, { params: { ...args } })
	}

	getLoyaltyTier(id) {
		return axios.get(`${this.jwtConfig.LoyaltyTiers}/${id}`)
	}

	createLoyaltyTier(args) {
		return axios.post(this.jwtConfig.LoyaltyTiers, args)
	}

	updateLoyaltyTier(id, args) {
		return axios.put(`${this.jwtConfig.LoyaltyTiers}/${id}`, args)
	}

	deleteLoyaltyTier(id) {
		return axios.delete(`${this.jwtConfig.LoyaltyTiers}/${id}`)
	}

	loyaltyTierFileUpload(formData) {
		return axios.post(`${this.jwtConfig.LoyaltyTiers}/file-upload`, formData)
	}

	getLoyaltyPointsList(args) {
		return axios.get(`${this.jwtConfig.LoyaltyPoints}`, { params: { ...args } })
	}

	getLoyaltyPoint(id) {
		return axios.get(`${this.jwtConfig.LoyaltyPoints}/${id}`)
	}

	createLoyaltyPoint(args) {
		return axios.post(this.jwtConfig.LoyaltyPoints, args)
	}

	updateLoyaltyPoint(id, args) {
		return axios.put(`${this.jwtConfig.LoyaltyPoints}/${id}`, args)
	}

	deleteLoyaltyPoint(id) {
		return axios.delete(`${this.jwtConfig.LoyaltyPoints}/${id}`)
	}

	getLoyaltyTierHistoryList(args) {
		return axios.get(`${this.jwtConfig.LoyaltyTierHistory}`, { params: { ...args } })
	}

	getLoyaltyPointHistoryList(args) {
		return axios.get(`${this.jwtConfig.LoyaltyPointHistory}`, { params: { ...args } })
	}

	getLoyaltyRewardHistoryList(args) {
		return axios.get(`${this.jwtConfig.LoyaltyRewardHistory}`, { params: { ...args } })
	}

	getPromotionCampaignsList(args) {
		return axios.get(`${this.jwtConfig.PromotionCampaigns}`, { params: { ...args } })
	}

	getPromotionRewardHistoryList(args) {
		return axios.get(`${this.jwtConfig.PromotionRewardHistory}`, { params: { ...args } })
	}

	getPromotionCampaign(id) {
		return axios.get(`${this.jwtConfig.PromotionCampaigns}/${id}`)
	}

	createPromotionCampaign(args) {
		return axios.post(this.jwtConfig.PromotionCampaigns, args)
	}

	updatePromotionCampaign(id, args) {
		return axios.put(`${this.jwtConfig.PromotionCampaigns}/${id}`, args)
	}

	deletePromotionCampaign(id) {
		return axios.delete(`${this.jwtConfig.PromotionCampaigns}/${id}`)
	}

	promotionCampaignFileUpload(formData) {
		return axios.post(`${this.jwtConfig.PromotionCampaigns}/file-upload`, formData)
	}

	/**
	 * Language
	 */
	getLanguagesList(args) {
		return axios.get(`${this.jwtConfig.Language}`, { params: { ...args } })
	}

	createLanguage(args) {
		return axios.post(this.jwtConfig.Language, args)
	}

	getLanguage(id) {
		return axios.get(`${this.jwtConfig.Language}/${id}`)
	}

	updateLanguage(id, args) {
		return axios.put(`${this.jwtConfig.Language}/${id}`, args)
	}

	deleteLanguage(id) {
		return axios.delete(`${this.jwtConfig.Language}/${id}`)
	}

	/**
	 * Language Word
	 */

	getLanguageWordsList(args) {
		return axios.get(`${this.jwtConfig.LanguageWords}`, { params: { ...args } })
	}

	createLanguageWord(args) {
		return axios.post(this.jwtConfig.LanguageWords, args)
	}

	getLanguageWordById(id) {
		return axios.get(`${this.jwtConfig.LanguageWords}/${id}`)
	}

	updateLanguageWord(id, args) {
		return axios.put(`${this.jwtConfig.LanguageWords}/${id}`, args)
	}

	deleteLanguageWord(id) {
		return axios.delete(`${this.jwtConfig.LanguageWords}/${id}`)
	}

	/**
	 * Rewards
	 */

	rewardsUpdate(...args) {
		return axios.post(this.jwtConfig.RewardsUpdate, ...args)
	}

	rewardsSymbolUpdate(...args) {
		return axios.post(this.jwtConfig.RewardsSymbolUpdate, ...args)
	}

	/**
	 * Dashboard
	 */
	getDashboardPlayerReport(args) {
		return axios.get(`${this.jwtConfig.DashboardReport}/player`, { params: { ...args } })
	}

	getDashboardBalanceReport(args) {
		return axios.get(`${this.jwtConfig.DashboardReport}/balance`, { params: { ...args } })
	}

	getDashboardOverviewChartReport(args) {
		return axios.get(`${this.jwtConfig.DashboardReport}/chart`, { params: { ...args } })
	}

	getDashboardCasinoReport(args) {
		return axios.get(`${this.jwtConfig.DashboardReport}/casino`, { params: { ...args } })
	}

	getDashboardSportsbookReport(args) {
		return axios.get(`${this.jwtConfig.DashboardReport}/sportsbook`, { params: { ...args } })
	}

	getDashboardOriginalReport(args) {
		return axios.get(`${this.jwtConfig.DashboardReport}/original`, { params: { ...args } })
	}

	getDashboardP2pReport(args) {
		return axios.get(`${this.jwtConfig.DashboardReport}/p2p`, { params: { ...args } })
	}

	getDashboardProfitReport(args) {
		return axios.get(`${this.jwtConfig.DashboardReport}/profit`, { params: { ...args } })
	}

	/**
	 * Player Report
	 */
	getPlayerReport(id, args) {
		return axios.get(`${this.jwtConfig.PlayerReport}/${id}`, { params: { ...args } })
	}

	getPlayerBalanceReport(id, args) {
		return axios.get(`${this.jwtConfig.PlayerReport}/${id}/balance`, { params: { ...args } })
	}

	getPlayerCasinoReport(id, args) {
		return axios.get(`${this.jwtConfig.PlayerReport}/${id}/casino`, { params: { ...args } })
	}

	getPlayerSportsReport(id, args) {
		return axios.get(`${this.jwtConfig.PlayerReport}/${id}/sportsbook`, { params: { ...args } })
	}

	getPlayerOriginalGameReport(id, args) {
		return axios.get(`${this.jwtConfig.PlayerReport}/${id}/original`, { params: { ...args } })
	}

	getPlayerP2pReport(id, args) {
		return axios.get(`${this.jwtConfig.PlayerReport}/${id}/p2p`, { params: { ...args } })
	}

	getPlayerOverviewChartReport(id, args) {
		return axios.get(`${this.jwtConfig.PlayerReport}/${id}/chart`, { params: { ...args } })
	}

	/**
	 * Files
	 */
	filesUpload(...args) {
		return axios.post(this.jwtConfig.Files, ...args)
	}

	filesDelete(...args) {
		return axios.post(`${this.jwtConfig.Files}delete`, ...args)
	}

	/**
	 * Setting
	 */

	getDashboards(...args) {
		return axios.get(`${this.jwtConfig.Settings}d-get`, ...args)
	}

	updateDashboards(...args) {
		return axios.post(`${this.jwtConfig.Settings}d-update`, ...args)
	}

	addPrivacy(...args) {
		return axios.post(`${this.jwtConfig.AddPrivacy}`, ...args)
	}

	getPrivacy(...args) {
		return axios.get(`${this.jwtConfig.GetPrivacy}`, ...args)
	}

	updatePrivacy(...args) {
		return axios.post(`${this.jwtConfig.UpdatePrivacy}`, ...args)
	}

	updateProfitAndVolume(...args) {
		return axios.post(`${this.jwtConfig.Settings}d-updateVP`, ...args)
	}

	/**
	 * SportsLists
	 */
	getSportsListsList(...args) {
		return axios.post(`${this.jwtConfig.SportsLists}/list`, ...args)
	}

	getSportsListsLabel(...args) {
		return axios.post(`${this.jwtConfig.SportsLists}/label`, ...args)
	}

	createSportsList(...args) {
		return axios.post(this.jwtConfig.SportsLists, ...args)
	}

	getSportsList(id) {
		return axios.get(`${this.jwtConfig.SportsLists}/${id}`)
	}

	getOneSportsHistory(id) {
		return axios.get(`${this.jwtConfig.SportsHistory}/${id}`)
	}

	updateOneSportsHistory(id, args) {
		return axios.put(`${this.jwtConfig.SportsHistory}/${id}`, args)
	}

	updateSportsList(id, args) {
		return axios.put(`${this.jwtConfig.SportsLists}/${id}`, args)
	}

	deleteSportsHistory(id) {
		return axios.delete(`${this.jwtConfig.SportsHistory}/${id}`)
	}

	deleteSportsList(id) {
		return axios.delete(`${this.jwtConfig.SportsLists}/${id}`)
	}

	/**
	 * SportsLeagues
	 */
	getSportsLeaguesList(...args) {
		return axios.post(`${this.jwtConfig.SportsLeagues}/list`, ...args)
	}

	getSportsLeaguesUpdate(...args) {
		return axios.post(`${this.jwtConfig.SportsLeagues}/update`, ...args)
	}

	createSportsLeague(...args) {
		return axios.post(this.jwtConfig.SportsLeagues, ...args)
	}

	getSportsLeague(id) {
		return axios.get(`${this.jwtConfig.SportsLeagues}/${id}`)
	}

	updateSportsLeague(id, args) {
		return axios.put(`${this.jwtConfig.SportsLeagues}/${id}`, args)
	}

	deleteSportsLeague(id) {
		return axios.delete(`${this.jwtConfig.SportsLeagues}/${id}`)
	}

	/**
	 * SportsMatchs
	 */
	getSportsMatchsList(...args) {
		return axios.post(`${this.jwtConfig.SportsMatchs}/list`, ...args)
	}

	getSportsMatchsUpdate(...args) {
		return axios.post(`${this.jwtConfig.SportsMatchs}/update`, ...args)
	}

	createSportsMatch(...args) {
		return axios.post(this.jwtConfig.SportsMatchs, ...args)
	}

	getSportsMatch(id) {
		return axios.get(`${this.jwtConfig.SportsMatchs}/${id}`)
	}

	updateSportsMatch(id, args) {
		return axios.put(`${this.jwtConfig.SportsMatchs}/${id}`, args)
	}

	deleteSportsMatch(id) {
		return axios.delete(`${this.jwtConfig.SportsMatchs}/${id}`)
	}

	/**
	 * SportsEndMatchs
	 */
	getSportsEndMatchsList(...args) {
		return axios.post(`${this.jwtConfig.SportsEndMatchs}/list`, ...args)
	}

	getSportsEndMatchsUpdate(...args) {
		return axios.post(`${this.jwtConfig.SportsEndMatchs}/update`, ...args)
	}

	createSportsEndMatch(...args) {
		return axios.post(this.jwtConfig.SportsEndMatchs, ...args)
	}

	getSportsEndMatch(id) {
		return axios.get(`${this.jwtConfig.SportsEndMatchs}/${id}`)
	}

	updateSportsEndMatch(id, args) {
		return axios.put(`${this.jwtConfig.SportsEndMatchs}/${id}`, args)
	}

	deleteSportsEndMatch(id) {
		return axios.delete(`${this.jwtConfig.SportsEndMatchs}/${id}`)
	}

	deleteAllSportsEndMatch() {
		return axios.delete(this.jwtConfig.SportsEndMatchs)
	}

	/**
	 * SportsFixMatchs
	 */
	getSportsFixMatchsList(...args) {
		return axios.post(`${this.jwtConfig.SportsFixMatchs}/list`, ...args)
	}

	getSportsFixMatchsUpdate(...args) {
		return axios.post(`${this.jwtConfig.SportsFixMatchs}/update`, ...args)
	}

	getSportsFixMatchsSettled(id) {
		return axios.post(`${this.jwtConfig.SportsFixMatchs}/${id}`)
	}

	createSportsFixMatch(...args) {
		return axios.post(this.jwtConfig.SportsFixMatchs, ...args)
	}

	getSportsFixMatch(id) {
		return axios.get(`${this.jwtConfig.SportsFixMatchs}/${id}`)
	}

	updateSportsFixMatch(id, args) {
		return axios.put(`${this.jwtConfig.SportsFixMatchs}/${id}`, args)
	}

	deleteSportsFixMatch(id) {
		return axios.delete(`${this.jwtConfig.SportsFixMatchs}/${id}`)
	}

	deleteAllSportsFixMatch() {
		return axios.delete(this.jwtConfig.SportsFixMatchs)
	}

	/**
	 * BracketsBets
	 */
	getBracketsBetsList(...args) {
		return axios.post(`${this.jwtConfig.BracketsBets}/list`, ...args)
	}

	createBracketsBet(...args) {
		return axios.post(this.jwtConfig.BracketsBets, ...args)
	}

	getBracketsBet(id) {
		return axios.get(`${this.jwtConfig.BracketsBets}/${id}`)
	}

	updateBracketsBet(id, args) {
		return axios.put(`${this.jwtConfig.BracketsBets}/${id}`, args)
	}

	deleteBracketsBet(id) {
		return axios.delete(`${this.jwtConfig.BracketsBets}/${id}`)
	}

	deleteAllBracketsBets() {
		return axios.delete(this.jwtConfig.BracketsBets)
	}

	/**
	 * BracketsMatchs
	 */
	getBracketsMatchsList(...args) {
		return axios.post(`${this.jwtConfig.BracketsMatchs}/list`, ...args)
	}

	createBracketsMatch(...args) {
		return axios.post(this.jwtConfig.BracketsMatchs, ...args)
	}

	getBracketsMatch(id) {
		return axios.get(`${this.jwtConfig.BracketsMatchs}/${id}`)
	}

	updateBracketsMatch(id, args) {
		return axios.put(`${this.jwtConfig.BracketsMatchs}/${id}`, args)
	}

	deleteBracketsMatch(id) {
		return axios.delete(`${this.jwtConfig.BracketsMatchs}/${id}`)
	}

	deleteAllBracketsMatchs() {
		return axios.delete(this.jwtConfig.BracketsMatchs)
	}

	/**
	 * SportsBet
	 */
	sportsResult(...args) {
		return axios.post(this.jwtConfig.SportsResult, ...args)
	}

	getSportsBetsList(...args) {
		return axios.post(`${this.jwtConfig.SportsBets}/list`, ...args)
	}

	getSportsBetsLabel(...args) {
		return axios.post(`${this.jwtConfig.SportsBets}/label`, ...args)
	}

	createSportsBet(...args) {
		return axios.post(this.jwtConfig.SportsBets, ...args)
	}

	getSportsBet(id) {
		return axios.get(`${this.jwtConfig.SportsBets}/${id}`)
	}

	updateSportsBet(id, args) {
		return axios.put(`${this.jwtConfig.SportsBets}/${id}`, args)
	}

	deleteSportsBet(id) {
		return axios.delete(`${this.jwtConfig.SportsBets}/${id}`)
	}

	/**
	 * SportsBetting
	 */
	getSportsBettingsList(...args) {
		return axios.post(`${this.jwtConfig.SportsBettings}/list`, ...args)
	}

	getSportsBettingsLabel(...args) {
		return axios.post(`${this.jwtConfig.SportsBettings}/label`, ...args)
	}

	getSportsBettingEvent(...args) {
		return axios.post(`${this.jwtConfig.SportsBettings}/events`, ...args)
	}

	createSportsBetting(...args) {
		return axios.post(this.jwtConfig.SportsBettings, ...args)
	}

	getSportsBetting(id) {
		return axios.get(`${this.jwtConfig.SportsBettings}/${id}`)
	}

	updateSportsBetting(id, args) {
		return axios.put(`${this.jwtConfig.SportsBettings}/${id}`, args)
	}

	deleteSportsBetting(id) {
		return axios.delete(`${this.jwtConfig.SportsBettings}/${id}`)
	}

	/**
	 * V2SportsBet
	 */
	getV2BetsHistory(...args) {
		return axios.post(`${this.jwtConfig.V2SportsList}/list`, ...args)
	}

	/**
	 * CasinoBet
	 */
	getCasinosHistorysList(args) {
		return axios.get(`${this.jwtConfig.CasinosHistory}`, { params: { ...args } })
	}

	/**
	 * Sportsbook History
	 */
	getSportsbookHistoryList(args) {
		return axios.get(`${this.jwtConfig.SportsbooksHistory}`, { params: { ...args } })
	}

	/**
	 * Fraud Detection
	 */
	getFraudIpList(args) {
		return axios.get(`${this.jwtConfig.FraudIp}`, { params: { ...args } })
	}

	blockFraudIp(args) {
		return axios.post(`${this.jwtConfig.FraudIp}/block`, args)
	}

	allowFraudIp(args) {
		return axios.post(`${this.jwtConfig.FraudIp}/allow`, args)
	}

	getFraudFpidList(args) {
		return axios.get(`${this.jwtConfig.FraudFpid}`, { params: { ...args } })
	}

	blockFraudFpid(args) {
		return axios.post(`${this.jwtConfig.FraudFpid}/block`, args)
	}

	allowFraudFpid(args) {
		return axios.post(`${this.jwtConfig.FraudFpid}/allow`, args)
	}

	getFraudIpBlacklistList(args) {
		return axios.get(`${this.jwtConfig.FraudIpBlacklist}`, { params: { ...args } })
	}

	createFraudIpBlacklist(args) {
		return axios.post(this.jwtConfig.FraudIpBlacklist, args)
	}

	blockFraudIpBlacklist(args) {
		return axios.post(`${this.jwtConfig.FraudIpBlacklist}/block`, args)
	}

	allowFraudIpBlacklist(args) {
		return axios.post(`${this.jwtConfig.FraudIpBlacklist}/allow`, args)
	}

	deleteFraudIpBlacklist(id) {
		return axios.delete(`${this.jwtConfig.FraudIpBlacklist}/${id}`)
	}

	getFraudFpidBlacklistList(args) {
		return axios.get(`${this.jwtConfig.FraudFpidBlacklist}`, { params: { ...args } })
	}

	createFraudFpidBlacklist(args) {
		return axios.post(this.jwtConfig.FraudFpidBlacklist, args)
	}

	blockFraudFpidBlacklist(args) {
		return axios.post(`${this.jwtConfig.FraudFpidBlacklist}/block`, args)
	}

	allowFraudFpidBlacklist(args) {
		return axios.post(`${this.jwtConfig.FraudFpidBlacklist}/allow`, args)
	}

	deleteFraudFpidBlacklist(id) {
		return axios.delete(`${this.jwtConfig.FraudFpidBlacklist}/${id}`)
	}

	/**
	 * Color game
	 */
	getColorgamesHistorysList(...args) {
		return axios.post(`${this.jwtConfig.ColorgameHistory}`, ...args)
	}

	getColorGameCategories(...args) {
		return axios.get(`${this.jwtConfig.ColorGameCategories}`, ...args)
	}

	addColorGame(...args) {
		return axios.post(`${this.jwtConfig.AddColorGame}`, ...args)
	}

	updateColorGameCategory(...args) {
		return axios.put(`${this.jwtConfig.UpdateColorGameCategory}`, ...args)
	}

	deleteColorGameCategory(id) {
		return axios.delete(`${this.jwtConfig.DeleteColorGameCategory}/${id}`)
	}
}
